import { useState, useMemo, useEffect, useContext } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { Combobox } from '@appkit4/react-components';
import { Upload } from '@appkit4/react-components/upload';
import { Tabs, Tab } from '@appkit4/react-components/tabs';
import NotificationBox, { NotificationMessage } from '@components/NotificationBox/NotififcationBox';
import { UserContext } from '@contexts/UserProvider';
import { inviteUser, bulkInviteUsers, removeUser } from '../../config/';
import { getAllUsers } from '../../config/getUserApis';
import { isUserAdmin } from '@utils/users';
import styles from './ManageUsers.module.scss';

interface ManageUsersProps {
    isShow: boolean;
    onCancel?: () => void;
    onSubmit?: (res: any) => void;
}

interface ManageUsersFooterProps {
    submitBtnDisabled?: boolean;
    submitBtnText: string;
    onSubmit: () => void;
    onCancel: () => void;
    showBulkActions?: boolean;
    onCopyEmails?: () => void;
    onBackToUpload?: () => void;
}

interface User {
    label: string;
    value: string;
    email: string;
    isOwner: boolean;
}

const ManageUsersFooter = ({
    submitBtnDisabled = false,
    submitBtnText,
    onSubmit,
    onCancel,
    showBulkActions = false,
    onCopyEmails,
    onBackToUpload,
}: ManageUsersFooterProps) => {
    if (showBulkActions) {
        return (
            <div className={styles['controls']}>
                <Button kind="primary" onClick={onBackToUpload}>
                    Back to upload
                </Button>
                <Button kind="secondary" onClick={onCopyEmails}>
                    Copy emails
                </Button>
                <Button kind="cancel" onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        );
    }

    return (
        <div className={styles['controls']}>
            <Button kind="primary" onClick={onSubmit} disabled={submitBtnDisabled}>
                {submitBtnText}
            </Button>
            <Button kind="cancel" onClick={onCancel}>
                Cancel
            </Button>
        </div>
    );
};

const ManageUsers = ({ isShow, onCancel = () => {}, onSubmit = () => {} }: ManageUsersProps) => {
    const [email, setEmail] = useState('');
    const [isTouched, setTouched] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [bulkResult, setBulkResult] = useState<any>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [notification, setNotification] = useState<NotificationMessage | null>(null);
    const [selectedUserStrArr, setSelectedUserStrArr] = useState<string[]>([]);
    const [usersArr, setUsersArr] = useState<User[]>([]);

    const { user, userEmail } = useContext(UserContext);

    const error = useMemo(() => {
        if (!email) return 'Please input an email address';
        if (!/^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)) {
            return 'Please enter a valid email address';
        }
        return '';
    }, [email]);

    const showNotification = (notif: NotificationMessage) => {
        setNotification(notif);
        if (notif.type === 'success') {
            setTimeout(() => setNotification(null), 5000);
        }
    };

    const clearNotification = () => setNotification(null);

    const refreshUserList = () => {
        getAllUsers().then((users) => {
            const list = users.map((u: any) => ({
                label: u.fullName,
                value: u.fullName,
                email: u.email,
                isOwner: false,
            }));
            setUsersArr(list.filter((u: any) => u.email !== userEmail));
        });
    };

    const handleCancel = () => {
        onCancel();
        setEmail('');
        setTouched(false);
        setBulkResult(null);
        setIsUploading(false);
        setSelectedFile(null);
        setNotification(null);
    };

    const userInvite = async () => {
        // Check if current user is admin
        if (!isUserAdmin(user)) {
            showNotification({
                type: 'error',
                title: 'Access Denied',
                message: 'Only administrators can invite users',
            });
            return;
        }

        if (activeIndex === 0) {
            const res = await inviteUser(email);

            showNotification({
                type: res.success ? 'success' : 'error',
                title: res.success ? 'Success' : 'Error',
                message: res.success ? 'User invited successfully' : 'User invite failed',
            });

            if (res.success) {
                setEmail('');
                setTouched(false);
            }

            onSubmit(res);
        } else if (activeIndex === 1) {
            if (!selectedFile) {
                showNotification({
                    type: 'warn',
                    title: 'No File Selected',
                    message: 'Please select a CSV file to upload',
                });
                return;
            }

            setIsUploading(true);

            try {
                const result = await bulkInviteUsers(selectedFile);

                if (result.success) {
                    setBulkResult(result.data);

                    if (result.data.failureCount === 0) {
                        showNotification({
                            type: 'success',
                            title: 'Success',
                            message: `All ${result.data.successCount} users invited successfully`,
                        });
                    } else {
                        const failedEmails =
                            result.data.failedInvites?.map((item: { email: string }) => item.email) || [];
                        const emailList = failedEmails.join(', ');

                        showNotification({
                            type: 'warn',
                            title: 'Partial Success',
                            message: `${result.data.successCount} succeeded, ${result.data.failureCount} failed`,
                            details: [emailList],
                        });
                    }

                    onSubmit(result);
                } else {
                    showNotification({
                        type: 'error',
                        title: 'Error',
                        message: result.error,
                    });
                }
            } catch (error: unknown) {
                const details =
                    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Failed inviting user';

                showNotification({
                    type: 'error',
                    title: 'Error',
                    message: `Failed to process CSV file\n${details}`,
                });
            } finally {
                setIsUploading(false);
                setSelectedFile(null);
            }
        }
    };

    const userRemove = async () => {
        // Check if current user is admin
        if (!isUserAdmin(user)) {
            showNotification({
                type: 'error',
                title: 'Access Denied',
                message: 'Only administrators can remove users',
            });
            return;
        }

        if (!selectedUserStrArr || (Array.isArray(selectedUserStrArr) && selectedUserStrArr.length === 0)) {
            showNotification({
                type: 'warn',
                title: 'No User Selected',
                message: 'Please select a user to remove',
            });
            return;
        }

        const emailsToRemove = Array.isArray(selectedUserStrArr) ? selectedUserStrArr : [selectedUserStrArr];

        try {
            const results = await Promise.all(emailsToRemove.map((email) => removeUser(email)));

            const successful = results.filter((r) => r.success).length;
            const failed = results.length - successful;

            if (failed === 0) {
                showNotification({
                    type: 'success',
                    title: 'Success',
                    message: `${successful} user${successful > 1 ? 's' : ''} removed successfully`,
                });

                setSelectedUserStrArr([]);

                refreshUserList();
            } else if (successful > 0) {
                showNotification({
                    type: 'warn',
                    title: 'Partial Success',
                    message: `${successful} succeeded, ${failed} failed`,
                });
                setSelectedUserStrArr([]);
                refreshUserList();
            } else {
                showNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to remove user(s)',
                });
            }
        } catch (error) {
            console.error('Error during user removal:', error);
            showNotification({
                type: 'error',
                title: 'Error',
                message: 'An error occurred while removing user(s)',
            });
        }
    };

    const handleEmailChange = (v: string) => {
        setTouched(true);
        setEmail(v);
    };

    const onTabChange = (i: number) => {
        setActiveIndex(i);
        setSelectedFile(null);
        setBulkResult(null);
        clearNotification();
    };

    const onChange = (file: any) => {
        const originFile = file instanceof File ? file : file?.originFile;
        setSelectedFile(originFile);
    };

    const handleResetUpload = () => {
        setBulkResult(null);
        setSelectedFile(null);
        clearNotification();
    };

    const handleCopyFailedEmails = () => {
        if (!bulkResult?.failedInvites) return;

        const csvContent = 'email\n' + bulkResult.failedInvites.map((item: { email: string }) => item.email).join('\n');

        navigator.clipboard.writeText(csvContent);

        showNotification({
            type: 'success',
            title: 'Copied',
            message: 'Failed emails copied to clipboard as CSV format',
        });
    };

    const handleComboboxSearch = (_value: string) => {
        console.info(_value);
    };

    const handleComboboxSelect = (value: any) => {
        setSelectedUserStrArr(value);
    };

    useEffect(() => {
        getAllUsers().then((users) => {
            const list = users.map((u: any) => ({
                label: u.fullName,
                value: u.fullName,
                email: u.email,
                isOwner: false,
            }));

            setUsersArr(list.filter((u: any) => u.email !== userEmail));
        });
    }, []);

    const getFooterConfig = () => {
        if (activeIndex === 0) {
            const hasValidEmail = email && !error;
            const hasSelectedUser =
                selectedUserStrArr && (Array.isArray(selectedUserStrArr) ? selectedUserStrArr.length > 0 : true);

            if (hasValidEmail && !hasSelectedUser) {
                return {
                    text: 'Invite user',
                    disabled: false,
                    action: userInvite,
                    showBulkActions: false,
                };
            } else if (hasSelectedUser) {
                return {
                    text: 'Remove user',
                    disabled: false,
                    action: userRemove,
                    showBulkActions: false,
                };
            } else {
                return {
                    text: 'Invite user',
                    disabled: true,
                    action: userInvite,
                    showBulkActions: false,
                };
            }
        } else {
            if (bulkResult && bulkResult.failureCount > 0) {
                return {
                    text: 'Back to upload',
                    disabled: false,
                    action: handleResetUpload,
                    showBulkActions: true,
                };
            }

            const isDisabled = !selectedFile || isUploading;
            return {
                text: 'Send invite',
                disabled: isDisabled,
                action: userInvite,
                showBulkActions: false,
            };
        }
    };

    const footerConfig = getFooterConfig();

    return (
        <Modal
            title="Manage users"
            visible={isShow}
            onCancel={handleCancel}
            maskCloseable={false}
            modalStyle={{ width: '600px', maxHeight: '90vh' }}
            footer={
                <ManageUsersFooter
                    onCancel={handleCancel}
                    onSubmit={footerConfig.action}
                    submitBtnText={footerConfig.text}
                    submitBtnDisabled={footerConfig.disabled}
                    showBulkActions={footerConfig.showBulkActions}
                    onCopyEmails={handleCopyFailedEmails}
                    onBackToUpload={handleResetUpload}
                />
            }>
            <div className={styles['modal-content']}>
                <Tabs type="underline" activeIndex={activeIndex} onTabChange={onTabChange} className={styles['tabs']}>
                    <Tab label="Email" value="Email" />
                    <Tab label="Bulk" value="Bulk" />
                </Tabs>

                {notification && <NotificationBox notification={notification} />}

                {activeIndex === 0 && (
                    <>
                        <h2>Invite user</h2>
                        <Input
                            title="Email address"
                            value={email}
                            className={styles['manage-users-input']}
                            onChange={handleEmailChange}
                            onBlur={() => setTouched(true)}
                            error={isTouched && !!error}
                            errorNode={<span className={styles['input-error']}>{isTouched ? error : ''}</span>}
                        />

                        <h2>Remove user</h2>
                        <Combobox
                            style={{ minWidth: '325px' }}
                            className="modal-combobox"
                            data={usersArr}
                            placeholder="Search for a user"
                            value={selectedUserStrArr}
                            valueKey="email"
                            onSearch={handleComboboxSearch}
                            onSelect={handleComboboxSelect}
                            dropdownRenderMode="portal"
                        />
                    </>
                )}

                {activeIndex === 1 && (
                    <>
                        <div className={styles['upload-container']}>
                            <Upload
                                onChange={onChange}
                                multiple={false}
                                autoUpload={false}
                                acceptFileType=".csv,.CSV"
                                uploadInstruction="You can upload CSV. The max file size is 10 MB."
                                maxFileSize={10 * 1024 * 1024}
                                config={{ type: 'inline', size: true }}
                            />

                            {selectedFile && (
                                <div className={styles['file-selected']}>
                                    <div className={styles['file-row']}>
                                        <span className="Appkit4-icon icon-document-file-outline" />
                                        <span className={styles['file-name']}>{selectedFile.name}</span>
                                        <span className={styles['file-size']}>
                                            {selectedFile.size >= 1024 * 1024
                                                ? `${(selectedFile.size / (1024 * 1024)).toFixed(1)}mb`
                                                : `${(selectedFile.size / 1024).toFixed(0)}kb`}
                                        </span>
                                        <span
                                            className="Appkit4-icon icon-check-circle"
                                            style={{ color: '#34a853', marginLeft: 'auto' }}
                                        />
                                    </div>

                                    <button
                                        className={styles['delete-attachment']}
                                        onClick={handleResetUpload}
                                        type="button">
                                        Delete attachment
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ManageUsers;
